import {
  Boolean,
  Dictionary,
  Literal,
  Null,
  Number,
  Optional,
  Partial,
  Array as RArray,
  Record as RRecord,
  Runtype,
  Static,
  String,
  Undefined,
  Union,
  Unknown,
} from "runtypes";
import { z } from "zod";

/**
 * Helper to create a runtype that accepts T, null, or undefined.
 * Use this instead of `T.Or(Null)` when you don't want to enforce
 * the distinction between null and undefined.
 */
export const Nullish = <T extends Runtype>(type: T) =>
  type.Or(Null).Or(Undefined);

import { ChartSpec } from "./chart/types.js";
import {
  RelativeDateOperationLiteral,
  RelativeDateUnitLiteral,
} from "./datetimeType.js";
import {
  BinaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
} from "./display-table/columnPredicateTypes.js";
import { DisplayTableOutput } from "./display-table/outputTypes.js";
import { CellType, MagicTypeAheadProviderLiteral } from "./enums.js";
import { assertNever, guardNever } from "./errors.js";
import { HqlAggregationFunctionLiteral } from "./hql/types.js";
import {
  DataConnectionId,
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  MagicApiKeyId,
  MagicEventId,
} from "./idTypeBrands.js";
import {
  AnthropicModelLiteral,
  AnthropicModelParams,
  GeminiEAPMultiRegionLiteral,
} from "./magic/anthropicTypes.js";
import {
  ChatCompletionsGateway,
  ChatCompletionsModelParams,
} from "./magic/chatCompletionsTypes.js";
import { Model } from "./magic/Model.js";
import { OpenAiModelParams } from "./magic/openAiTypes.js";
import { getNormalEnum } from "./runtypeEnums.js";
import { VariableName } from "./typeBrands.js";

// The date that Magic Terms of Service became part of the Hex Service Agreement
// All orgs created after this date have auto accepted the Magic TOS since its part of the default Hex Service Agreement
export const MAGIC_TERMS_PART_OF_SERVICE_AGREEMENT_DATE = new Date(
  "2025-02-26T00:00:00.000Z",
);
export function wasOrgCreatedAfterMagicTermsPartOfServiceAgreementDate(
  orgCreatedDateString: Date | string,
): boolean {
  return (
    new Date(orgCreatedDateString) > MAGIC_TERMS_PART_OF_SERVICE_AGREEMENT_DATE
  );
}

export const MagicSupportedImageMimeTypes = Union(
  Literal("image/png"),
  Literal("image/jpeg"),
);
export type MagicSupportedImageMimeTypes = Static<
  typeof MagicSupportedImageMimeTypes
>;

export const MagicSupportedDocumentMimeTypes = Union(
  Literal("application/pdf"),
);
export type MagicSupportedDocumentMimeTypes = Static<
  typeof MagicSupportedDocumentMimeTypes
>;

export const MAGIC_SUPPORTED_IMAGE_ACCEPT: Record<
  MagicSupportedImageMimeTypes,
  string[]
> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

export const MagicSupportedTextMimeTypes = Union(
  Literal("text/plain"),
  Literal("text/markdown"),
  Literal("text/csv"),
  Literal("text/tab-separated-values"),
  Literal("text/html"),
  Literal("text/css"),
  Literal("text/xml"),
  Literal("text/javascript"),
  Literal("text/yaml"),
  Literal("application/json"),
  Literal("application/xml"),
  Literal("application/javascript"),
  Literal("application/x-python"),
  Literal("application/x-yaml"),
  Literal("application/yaml"),
  Literal("application/x-sh"),
);
export type MagicSupportedTextMimeTypes = Static<
  typeof MagicSupportedTextMimeTypes
>;

export const MAGIC_SUPPORTED_TEXT_ACCEPT: Record<
  MagicSupportedTextMimeTypes,
  string[]
> = {
  "text/plain": [".txt"],
  "text/markdown": [".md"],
  "text/csv": [".csv"],
  "text/tab-separated-values": [".tsv"],
  "text/html": [".html", ".htm"],
  "text/css": [".css"],
  "text/xml": [".xml"],
  "text/javascript": [".js"],
  "text/yaml": [".yaml", ".yml"],
  "application/json": [".json"],
  "application/xml": [".xml"],
  "application/javascript": [".js"],
  "application/x-python": [".py"],
  "application/x-yaml": [".yaml", ".yml"],
  "application/yaml": [".yaml", ".yml"],
  "application/x-sh": [".sh"],
};

/**
 * Maximum file size for text files that can be read by agents (5MB).
 * This is a memory safeguard during file loading. The actual output to the agent
 * is bounded by line limits and truncation to avoid bloating the context.
 */
export const MAGIC_MAX_TEXT_FILE_SIZE = 5 * 1024 * 1024;

export const MAGIC_SUPPORTED_FILE_EXTENSIONS: ReadonlySet<string> = new Set(
  [
    ...Object.values(MAGIC_SUPPORTED_IMAGE_ACCEPT).flat(),
    ...Object.values(MAGIC_SUPPORTED_TEXT_ACCEPT).flat(),
  ].map((ext) => ext.toLowerCase()),
);
export const SupportedExploreIntentLiteral = Union(
  Literal("SUPPORTED_EXPLORATION"),
);
export const UnsupportedIntentLiteral = Union(
  Literal("UNSUPPORTED_EXPLORATION"),
  Literal("QUESTION"),
  Literal("VISUAL_DETAIL_EDIT"),
  Literal("UNSUPPORTED_VISUALIZATION"),
);
export const ExploreIntentLiteral = Union(
  ...SupportedExploreIntentLiteral.alternatives,
  ...UnsupportedIntentLiteral.alternatives,
);
export type ExploreIntent = Static<typeof ExploreIntentLiteral>;
export const ExploreIntent = getNormalEnum(ExploreIntentLiteral);

export const EXPLORE_INTENT_MESSAGE_MAP: Record<ExploreIntent, string | null> =
  {
    [ExploreIntent.SUPPORTED_EXPLORATION]: null,
    [ExploreIntent.VISUAL_DETAIL_EDIT]:
      "Sorry, we can't change the chart styling with AI yet.",
    [ExploreIntent.UNSUPPORTED_EXPLORATION]:
      "The data required to answer this question is unavailable or unsupported.",
    [ExploreIntent.QUESTION]:
      "Sorry, we can't answer general questions with AI yet.",
    [ExploreIntent.UNSUPPORTED_VISUALIZATION]:
      "That visualization type isn't supported yet.",
  };

export const PromptTemplateTypeLiteral = Union(
  Literal("APPEND_MARKDOWN"),
  Literal("APPEND_PYTHON"),
  Literal("APPEND_R"),
  Literal("DOCUMENT_PYTHON"),
  Literal("DOCUMENT_R"),
  Literal("DOCUMENT_SQL"),
  Literal("EDIT_MARKDOWN"),
  Literal("EDIT_PYTHON_DIFF"),
  Literal("EDIT_PYTHON_ADIFF"),
  Literal("EDIT_R"),
  Literal("EDIT_SQL_DIFF"),
  Literal("EDIT_SQL_ADIFF"),
  Literal("GENERATE_EXPLORE"),
  Literal("FIX_MARKDOWN"),
  Literal("FIX_PYTHON_BASIC"),
  Literal("FIX_PYTHON_DIFF"),
  Literal("FIX_PYTHON_ADIFF"),
  Literal("FIX_R"),
  Literal("FIX_SQL_DIFF"),
  Literal("FIX_SQL_ADIFF"),
  Literal("FIX_SQL_BASIC"),
  Literal("GENERATE_MARKDOWN"),
  Literal("GENERATE_PYTHON"),
  Literal("GENERATE_R"),
  Literal("GENERATE_SQL"),
  Literal("NAME_SQL_CELL"),
  Literal("NAME_PYTHON_CELL"),
  Literal("NAME_R_CELL"),
  Literal("NAME_MARKDOWN_CELL"),
  Literal("NAME_SQL_CELL_OUTPUT"),
  Literal("NAME_CHART_CELL_OUTPUT"),
  Literal("GENERATE_PROJECT_DESCRIPTION"),
  Literal("GENERATE_PROJECT_HYDE"),
  Literal("NAME_PROJECT"),
  Literal("NAME_PROJECT_FROM_PROMPT"),
  Literal("SIMPLE_CHART"),
  Literal("EDIT_CHART"),
  Literal("NAME_CHART_CELL"),
  Literal("SUMMARIZE_SCHEDULED_RUN"),
);

/**
 * @deprecated Use PromptTemplateV2 instead, with the modelFeatureKey system
 * rather than the old primaryAgentConfigOverride system. If in doubt, ask
 * @johnwiseheart for help.
 */
export type PromptTemplateType = Static<typeof PromptTemplateTypeLiteral>;
export const PromptTemplateType = getNormalEnum(PromptTemplateTypeLiteral);

export const GenerateExploreTemplateLiteral = Union(
  Literal("GENERATE_EXPLORE"),
);
export type GenerateExploreTemplate = Static<
  typeof GenerateExploreTemplateLiteral
>;
export const GenerateExploreTemplate = getNormalEnum(
  GenerateExploreTemplateLiteral,
);

export const MentionedDataframeName = String.withBrand(
  "MentionedDataframeName",
);
export type MentionedDataframeName = Static<typeof MentionedDataframeName>;

export const MagicCellPayload = Union(ChartSpec);
export type MagicCellPayload = Static<typeof MagicCellPayload>;

export const MagicRequestModeLiteral = Union(
  Literal("GENERATE"),
  Literal("EDIT"),
  Literal("MORE_CELLS"),
  Literal("WRITEBACK"),
  Literal("MARKDOWN"),
  Literal("INPUT"),
  Literal("DATA_READ"),
  Literal("PREV_CELL_CODE"),
  Literal("QUESTION"),
);
export type MagicRequestMode = Static<typeof MagicRequestModeLiteral>;
export const MagicRequestMode = getNormalEnum(MagicRequestModeLiteral);

export const SingleCellMagicEditTypeLiteral = Union(
  Literal(CellType.CODE),
  Literal(CellType.MARKDOWN),
  Literal(CellType.SQL),
);

export const SingleCellMagicEditType = getNormalEnum(
  SingleCellMagicEditTypeLiteral,
);
export type SingleCellMagicEditType = Static<
  typeof SingleCellMagicEditTypeLiteral
>;

// Magic cell types that only Hex knows about
export const HexMagicCellTypeLiteral = Union(
  Literal(CellType.TEXT),
  Literal(CellType.METRIC),
  Literal(CellType.EXPLORE),
);
export const HexMagicCellType = getNormalEnum(HexMagicCellTypeLiteral);
export type HexMagicCellType = Static<typeof HexMagicCellTypeLiteral>;

export const MagicCellTypeLiteral = Union(
  Literal(CellType.CODE),
  Literal(CellType.MARKDOWN),
  Literal(CellType.SQL),
  Literal(CellType.CHART),
  Literal(CellType.EXPLORE),

  ...HexMagicCellTypeLiteral.alternatives,
);
export const MagicCellType = getNormalEnum(MagicCellTypeLiteral);
export type MagicCellType = Static<typeof MagicCellTypeLiteral>;

export const MagicNonStreamingCellType = Union(Literal(MagicCellType.CHART));
export type MagicNonStreamingCellType = Static<
  typeof MagicNonStreamingCellType
>;
export const MagicStreamingCellType = Union(
  Literal(MagicCellType.MARKDOWN),
  Literal(MagicCellType.CODE),
  Literal(MagicCellType.SQL),
);
export type MagicStreamingCellType = Static<typeof MagicStreamingCellType>;

export const MagicEventSourceLiteral = Union(
  // [PROJECT LOGIC]
  // Any magic action originally from within an individual cell.
  Literal("CELL"),
  // Any magic action originating from within a project, but not an individual cell
  Literal("PROJECT"),
  // Any magic action originating from the "Add Cells" prompt bar in project logic.
  Literal("ADD_CELLS"),
  // Any magic action originating from an empty project's zero state bar.
  Literal("EMPTY_PROJECT"),

  // [EXPLORE]
  // Any magic action originating from within the Explore From Scratch editor.
  Literal("EXPLORE"),
  // Any magic action originating from within the Explore From Here editor.
  Literal("EXPLORE_FROM_HERE"),
  // Any magic action originating from Slack.
  Literal("SLACK"),
  // Any magic action originating from "Ask" (NOT Ask Magic)
  Literal("ASK"),

  // [SEMANTIC AUTHORING]
  // Any magic action originating from within the Semantic Authoring workbench.
  Literal("SEMANTIC_AUTHORING"),

  // Any magic action originating from the Eval / Research team.
  Literal("EVAL"),
  // When magic pauses saying "I can't do that", and the user types in a new prompt
  Literal("UNSUPPORTED_PAUSE"),
  // Any magic action that originates from the home page - this could include "Ask Magic"
  Literal("HOME"),

  // [DEPRECATED]
  Literal("CONTINUE_FROM_CHAIN"), // not used anywhere
  Literal("TR_REGEN"),
);
export type MagicEventSource = Static<typeof MagicEventSourceLiteral>;
export const MagicEventSource = getNormalEnum(MagicEventSourceLiteral);

export const isExploreSource = (source: MagicEventSource | null): boolean =>
  source === "EXPLORE" || source === "EXPLORE_FROM_HERE";

export const MagicCellEditTypeLiteral = Union(
  Literal("CUSTOM"),
  Literal("INSERT"),
  Literal("FIX_ERRORS"),
  Literal("BASIC_SYNTAX_FIX"),
  Literal("DOCUMENT"),
  Literal("FIX_SPELLING_AND_GRAMMAR"),
);
export type MagicCellEditType = Static<typeof MagicCellEditTypeLiteral>;
export const MagicCellEditType = getNormalEnum(MagicCellEditTypeLiteral);

export const MagicCellActionLiteral = Union(
  Literal("NAME_CELL"),
  Literal("NAME_CELL_OUTPUT"),
);
export type MagicCellAction = Static<typeof MagicCellActionLiteral>;
export const MagicCellAction = getNormalEnum(MagicCellActionLiteral);

export const MagicProjectActionLiteral = Union(
  Literal("DESCRIBE_PROJECT"),
  Literal("NAME_PROJECT"),
);
export type MagicProjectAction = Static<typeof MagicProjectActionLiteral>;
export const MagicProjectAction = getNormalEnum(MagicProjectActionLiteral);

export const MagicSystemActionLiteral = Union(Literal("HYDE_PROJECT"));
export type MagicSystemAction = Static<typeof MagicSystemActionLiteral>;
export const MagicSystemAction = getNormalEnum(MagicSystemActionLiteral);

// Magic keywords exclusively associated with the response from
// a prompt template/workflow based run.
export const MagicPromptTemplateKeywordLiteral = Union(
  Literal("SUMMARIZE_SCHEDULED_RUN"),
  ...MagicCellEditTypeLiteral.alternatives,
  ...MagicCellActionLiteral.alternatives,
  ...MagicProjectActionLiteral.alternatives,
  ...MagicSystemActionLiteral.alternatives,
);
export type MagicPromptTemplateKeyword = Static<
  typeof MagicPromptTemplateKeywordLiteral
>;
export const MagicPromptTemplateKeyword = getNormalEnum(
  MagicPromptTemplateKeywordLiteral,
);

// Magic keywords exclusively associated with the response from
// an agentic run.
export const MagicAgentKeywordLiteral = Union(
  Literal("AGENT_RESPONSE"),
  Literal("AGENT_TOOL_CALL"),
  Literal("AGENT_COMPRESSION"),
);
export type MagicAgentKeyword = Static<typeof MagicAgentKeywordLiteral>;
export const MagicAgentKeyword = getNormalEnum(MagicAgentKeywordLiteral);

export const MagicKeywordLiteral = Union(
  ...MagicPromptTemplateKeywordLiteral.alternatives,
  ...MagicAgentKeywordLiteral.alternatives,
);
export type MagicKeyword = Static<typeof MagicKeywordLiteral>;
export const MagicKeyword = getNormalEnum(MagicKeywordLiteral);

// Magic keywords that should not be reflected as a `latestMagicEvent` for a cell
export const ExcludedCellMagicKeywordLiteral = Union(
  Literal(MagicKeyword.NAME_CELL),
  Literal(MagicKeyword.NAME_CELL_OUTPUT),
);
export type ExcludedCellMagicKeyword = Static<
  typeof ExcludedCellMagicKeywordLiteral
>;
export const ExcludedCellMagicKeyword = getNormalEnum(
  ExcludedCellMagicKeywordLiteral,
);
export const SystemMagicEventStatusLiteral = Union(
  Literal("CREATED"),
  Literal("PROCESSING"),
  Literal("FAILED"),
  Literal("COMPLETED"),
);
export type SystemMagicEventStatus = Static<
  typeof SystemMagicEventStatusLiteral
>;
export const SystemMagicEventStatus = getNormalEnum(
  SystemMagicEventStatusLiteral,
);

export const SystemMagicEventSourceLiteral = Union(
  Literal("SERVER"),
  Literal("EVAL"),
);
export type SystemMagicEventSource = Static<
  typeof SystemMagicEventSourceLiteral
>;
export const SystemMagicEventSource = getNormalEnum(
  SystemMagicEventSourceLiteral,
);
export const isEventPendingReview = (
  status?: MagicEventStatus | null,
): boolean =>
  status != null &&
  (status === MagicEventStatus.PENDING_REVIEW ||
    status === MagicEventStatus.ERROR_WITH_FALLBACK);

export const MagicEventStatusLiteral = Union(
  Literal("LOADING"),
  Literal("ERROR"),
  // Use this state when we fallback to a safe default, if the LLM generated result cannot be used (some error occurred).
  // For Explore, the fallback is generated at runtime, to maintain the event."result" for debugging and consistent with what the LLM responded with.
  Literal("ERROR_WITH_FALLBACK"),
  // The Magic Event is completed and potentially waiting user action to accept.
  Literal("PENDING_REVIEW"),
  Literal("CANCELLED"),
  // Used when an event is reviewed before applying - useful for Magic Analysis to 'accept' the new cells.
  Literal("REVIEWED"),
  // Indicates that the Magic Event previously failed and was retried. A new Magic Event would be generated.
  Literal("RETRIED"),
);
export type MagicEventStatus = Static<typeof MagicEventStatusLiteral>;
export const MagicEventStatus = getNormalEnum(MagicEventStatusLiteral);

// #region Errors & Warnings

// Special failure events that we show as warnings
// in the UI because a partial result was still achieved.
export const MagicEventWarningLiteral = Union(
  Literal("JOIN_VALIDATION_TIMEOUT"),
);

export const MagicEventFailureLiteral = Union(
  Literal("NO_CONTEXT"),
  Literal("BAD_DATA"),
  Literal("AGENT_ERROR"),
  Literal("AGENT_OVERLOADED"),
  Literal("GENERIC_ERROR"),
  Literal("POSSIBLE_PROMPT_INJECTION"),
  Literal("OVER_LIMIT"),
  Literal("NO_DATA"),
  Literal("INVALID_RESPONSE"),
  Literal("HALLUCINATED_SCHEMA_ELEMENT"),
  Literal("NON_EXPRESSIBLE"),
  Literal("INVALID_JOIN"),
  Literal("EXPLORE_TIMED_OUT"),
  Literal("EXPLORE_ERRORED"),
  Literal("EXPLORE_TABLE_SWAPPED"),
  Literal("UNSUPPORTED_QUERY_ELEMENTS"), // agent wrote SQL for an explore that cannot be expressed in explore UX
  Literal("EXPLORE_NO_DATA"),
  Literal("CHART_ROW_LIMIT_EXCEEDED"),
  Literal("BYOK_AUTHENTICATION"), // Received an authentication error from the provider for a customer's key
  Literal("BYOK_KEY_MISSING"), // Org requires its own key (BYOK) but none is configured for the requested client type
  Literal("AGENT_COMPRESSION_ERROR"), // Error occurred while compressing the conversation
  Literal("TOKEN_COUNTING_FAILURE"), // Error occurred while counting tokens
  Literal("REQUEST_CANCELLED"), // Request was cancelled
  Literal("INVALID_MESSAGE_HISTORY"), // Message history is got corrupted
  Literal("INVALID_REQUEST"), // 400 error from provider due to malformed request (e.g., invalid base64)
  Literal("DATA_CONNECTION_NOT_AUTHORIZED"), // User lacks query access to a data connection the agent needs to use
  Literal("MODEL_NOT_AVAILABLE"), // Provider key/config can't access the requested model (e.g. a BYOK key that doesn't expose it)
  Literal("PROVIDER_CONNECTION_INTERRUPTED"), // Network/transport failure talking to the provider (e.g. ECONNRESET, read timeout, mid-stream socket close)
  Literal("AGENT_AUTH"), // Authentication/authorization error from the provider (e.g. a 401/403 for a bad, missing, or expired key)
  Literal("PROVIDER_QUOTA_EXCEEDED"), // The provider account is out of funds/quota (e.g. OpenAI insufficient_quota, Anthropic "credit balance is too low")
);

export const MagicEventFailureReasonLiteral = Union(
  ...MagicEventFailureLiteral.alternatives,
  ...MagicEventWarningLiteral.alternatives,
);

export type MagicEventFailureReason = Static<
  typeof MagicEventFailureReasonLiteral
>;
export const MagicEventFailureReason = getNormalEnum(
  MagicEventFailureReasonLiteral,
);

export const MagicCellRunResultLiteral = Union(
  Literal("SUCCESS"), // cell ran successfully
  Literal("ERROR"), // cell execution errored
  Literal("TIMEOUT"), // cell took too long to run
  Literal("CANCELLED"), // cell execution was cancelled (e.g. kernel interrupt)
  Literal("NO_DATA"), // sql cell returned no data
  Literal("FAILURE"), // failure that should cause the whole MA to error (e.g. no context)
);
export type MagicCellRunResult = Static<typeof MagicCellRunResultLiteral>;
export const MagicCellRunResult = getNormalEnum(MagicCellRunResultLiteral);

export const TEXT_TOO_LONG_ERROR =
  "AI features only support up to ~3,000 words. Try splitting this cell into multiple cells.";

export const MODEL_OVERLOADED_ERROR =
  "Our model provider is having issues or is at capacity. Try again in a few minutes.";

export const MAGIC_PROVIDER_ERROR =
  "Our model provider is having problems. We will be back as soon as they are!";

export const BYOK_AUTHENTICATION_ERROR =
  "We had a problem authorizing the request with your workspace's API key. Please reach out to an Admin to have them replace the credentials.";

export const BYOK_KEY_MISSING_ERROR =
  "This workspace is configured to use its own API key, but none is set up. Ask a workspace admin to add an API key in the workspace's model settings.";

export const GENERIC_MAGIC_ERROR =
  "Something went wrong. Please try again, or contact support if the problem persists.";

export const MAGIC_BAD_PROMPT_ERROR =
  "Your request couldn't be processed. Try rephrasing or providing more specific instructions.";

export const MAGIC_OVER_USAGE_ERROR =
  "You've reached your AI usage limit. Upgrade your plan to continue.";

export const MAGIC_NO_CONTEXT_ERROR =
  "No relevant data was found for your request. Try rephrasing or verifying the data source.";

export const MAGIC_NO_RESULTS =
  "SQL cell returned no data. Review your results before resuming.";

export const MAGIC_BAD_DATA =
  "Unable to create chart with the current data selection.";

export const MAGIC_INVALID_RESPONSE =
  "We couldn't generate a valid response for your request. Try rephrasing or providing more specific instructions.";

export const MAGIC_UNSUPPORTED_QUERY_ELEMENTS =
  "The provided request cannot be expressed in an Exploration.";

const MAGIC_JOIN_VALIDATION_ERROR =
  "There was an issue validating your join. Check column compatibility or try a different join condition.";

const MAGIC_JOIN_VALIDATION_TIMEOUT =
  "We were unable to validate this join. Double check your data's accuracy.";

const MAGIC_NO_SUMMARY_FROM_EXPLORE_TIMEOUT =
  "Your exploration took too long to run. We were unable to generate a summary.";

const MAGIC_GENERATED_EXPLORE_ERRORED =
  "We tried to generate an exploration, but the combination of filters and transformations applied resulted in an error. Try checking and adjusting the column assignments and filter values.";

const MAGIC_SUMMARY_FROM_EXPLORE_TABLE_SWAPPED =
  "We were unable to find relevant data to answer your question. Try picking a different data source.";

const MAGIC_EXPLORE_NO_DATA =
  "Your exploration returned no data. Consider removing a filter or changing your question.";

const MAGIC_EXPLORE_TOO_MANY_ROWS =
  "Your exploration returned too many rows of data. Consider adding a filter or changing your question.";

const MAGIC_AGENT_COMPRESSION_ERROR =
  "Context summarization failed. The most recent message may be too large to summarize. Please send another message to try again.";

const MAGIC_INVALID_MESSAGE_HISTORY =
  "There's an issue with your thread. Consider creating a new thread to continue.";

// This isn't the ideal error message, because we don't actually know what the token counting failure
// implies yet. Maybe it implies the provider is down, but I'm not totally sure. For now, we'll just
// show a generic error and we can update it later.
const MAGIC_TOKEN_COUNTING_FAILURE =
  "Something went wrong. Please try again, or contact support if the problem persists.";

const MAGIC_REQUEST_CANCELLED = "The request was cancelled.";

const MAGIC_DATA_CONNECTION_NOT_AUTHORIZED =
  "You don't have permission to use one of the data connections this app relies on. Contact the project owner or your workspace admin for access.";

const MAGIC_MODEL_NOT_AVAILABLE =
  "The selected model isn't available with this workspace's AI configuration. If your workspace uses its own API key (BYOK), ask an admin to confirm the key can access this model in the workspace's model settings. Otherwise, try selecting a different model.";

const MAGIC_PROVIDER_CONNECTION_INTERRUPTED =
  "The connection to the model provider was interrupted. This is usually temporary — please try again.";

const MAGIC_AGENT_AUTH =
  "We couldn't authenticate with the model provider. If your workspace uses its own API key (BYOK), ask an admin to verify it in the workspace's model settings, otherwise please contact support.";

const MAGIC_PROVIDER_QUOTA_EXCEEDED =
  "The model provider rejected the request because its account is out of credits or has hit a billing limit. If your workspace uses its own API key (BYOK), ask an admin to add funds or raise the limit in the provider's billing settings, otherwise please contact support.";

export const MagicErrorTypeMessageMap: Record<MagicEventFailureReason, string> =
  {
    [MagicEventFailureReason.AGENT_OVERLOADED]: MODEL_OVERLOADED_ERROR,
    [MagicEventFailureReason.GENERIC_ERROR]: GENERIC_MAGIC_ERROR,
    [MagicEventFailureReason.POSSIBLE_PROMPT_INJECTION]: MAGIC_BAD_PROMPT_ERROR,
    [MagicEventFailureReason.AGENT_ERROR]: MAGIC_PROVIDER_ERROR,
    [MagicEventFailureReason.NO_CONTEXT]: MAGIC_NO_CONTEXT_ERROR,
    [MagicEventFailureReason.OVER_LIMIT]: MAGIC_OVER_USAGE_ERROR,
    [MagicEventFailureReason.NO_DATA]: MAGIC_NO_RESULTS,
    [MagicEventFailureReason.BAD_DATA]: MAGIC_BAD_DATA,
    [MagicEventFailureReason.INVALID_RESPONSE]: MAGIC_INVALID_RESPONSE,
    [MagicEventFailureReason.UNSUPPORTED_QUERY_ELEMENTS]:
      MAGIC_UNSUPPORTED_QUERY_ELEMENTS,
    [MagicEventFailureReason.NON_EXPRESSIBLE]: MAGIC_UNSUPPORTED_QUERY_ELEMENTS,
    [MagicEventFailureReason.HALLUCINATED_SCHEMA_ELEMENT]: GENERIC_MAGIC_ERROR, // Should never be shown to the user
    [MagicEventFailureReason.JOIN_VALIDATION_TIMEOUT]:
      MAGIC_JOIN_VALIDATION_TIMEOUT,
    [MagicEventFailureReason.INVALID_JOIN]: MAGIC_JOIN_VALIDATION_ERROR,
    [MagicEventFailureReason.EXPLORE_TIMED_OUT]:
      MAGIC_NO_SUMMARY_FROM_EXPLORE_TIMEOUT,
    [MagicEventFailureReason.EXPLORE_ERRORED]: MAGIC_GENERATED_EXPLORE_ERRORED,
    [MagicEventFailureReason.EXPLORE_TABLE_SWAPPED]:
      MAGIC_SUMMARY_FROM_EXPLORE_TABLE_SWAPPED,
    [MagicEventFailureReason.EXPLORE_NO_DATA]: MAGIC_EXPLORE_NO_DATA,
    [MagicEventFailureReason.CHART_ROW_LIMIT_EXCEEDED]:
      MAGIC_EXPLORE_TOO_MANY_ROWS,
    [MagicEventFailureReason.BYOK_AUTHENTICATION]: BYOK_AUTHENTICATION_ERROR,
    [MagicEventFailureReason.BYOK_KEY_MISSING]: BYOK_KEY_MISSING_ERROR,
    [MagicEventFailureReason.AGENT_COMPRESSION_ERROR]:
      MAGIC_AGENT_COMPRESSION_ERROR,
    [MagicEventFailureReason.TOKEN_COUNTING_FAILURE]:
      MAGIC_TOKEN_COUNTING_FAILURE,
    [MagicEventFailureReason.REQUEST_CANCELLED]: MAGIC_REQUEST_CANCELLED,
    [MagicEventFailureReason.INVALID_MESSAGE_HISTORY]:
      MAGIC_INVALID_MESSAGE_HISTORY,
    [MagicEventFailureReason.INVALID_REQUEST]: GENERIC_MAGIC_ERROR,
    [MagicEventFailureReason.DATA_CONNECTION_NOT_AUTHORIZED]:
      MAGIC_DATA_CONNECTION_NOT_AUTHORIZED,
    [MagicEventFailureReason.MODEL_NOT_AVAILABLE]: MAGIC_MODEL_NOT_AVAILABLE,
    [MagicEventFailureReason.PROVIDER_CONNECTION_INTERRUPTED]:
      MAGIC_PROVIDER_CONNECTION_INTERRUPTED,
    [MagicEventFailureReason.AGENT_AUTH]: MAGIC_AGENT_AUTH,
    [MagicEventFailureReason.PROVIDER_QUOTA_EXCEEDED]:
      MAGIC_PROVIDER_QUOTA_EXCEEDED,
  };

// #endregion Errors & Warnings

export const MagicTableReference = RRecord({
  db: String.Or(Null),
  schema: String,
  table: String,
});
export type MagicTableReference = Static<typeof MagicTableReference>;

export const MagicExploreTableInfo = RRecord({
  baseTable: MagicTableReference,
  joinTables: RArray(MagicTableReference),
});
export type MagicExploreTableInfo = Static<typeof MagicExploreTableInfo>;

export const MagicHydeQuestion = String;
export type MagicHydeQuestion = Static<typeof MagicHydeQuestion>;

// NB - this is part of the function payload sent to the LLM; use caution if modifying,
// as it could affect response quality
export const MentionedDataframeNameSchema = RArray(
  RRecord({ name: String, type: String }),
);
export type MentionedDataframeNameSchema = Static<
  typeof MentionedDataframeNameSchema
>;

export const VariableSpec = RRecord({
  name: String,
  type: String,
  dataframeSchema: Optional(MentionedDataframeNameSchema),
});
export type VariableSpec = Static<typeof VariableSpec>;

export const MagicPromptGenerationCellSpec = RRecord({
  type: MagicCellTypeLiteral,
  cellTitle: String,
  inputVariables: RArray(VariableSpec),
  outputVariables: RArray(VariableSpec),
  prompt: String,
});

export type MagicPromptGenerationCellSpec = Static<
  typeof MagicPromptGenerationCellSpec
>;

export const HexMagicApiKeySentinel = "HEX_DEFAULT" as const;
export type HexMagicApiKeySentinel = typeof HexMagicApiKeySentinel;

export const MagicEventMagicApiKey = Union(
  Literal(HexMagicApiKeySentinel),
  MagicApiKeyId,
);
export type MagicEventMagicApiKey = Static<typeof MagicEventMagicApiKey>;

export const RealAiClientTypeLiteral = Union(
  Literal("OPENAI"),
  Literal("ANTHROPIC"),
  /**
   * Streaming OpenAI Chat Completions API (`/v1/chat/completions`).
   */
  Literal("OPENAI_CHAT_COMPLETIONS"),
  /**
   * Non-streaming OpenAI Chat Completions API (`/v1/chat/completions`).
   */
  Literal("OPENAI_TYPEAHEAD"),
);
export type RealAiClientType = Static<typeof RealAiClientTypeLiteral>;
export const RealAiClientType = getNormalEnum(RealAiClientTypeLiteral);

export const AiClientTypeLiteral = Union(
  ...RealAiClientTypeLiteral.alternatives,
  Literal("MOCK"),
);
/**
 * Represents the different kinds of clients we support for AI calls.
 *
 * This is **not** necessarily the provider. For example, a AiClientType.OPENAI
 * does not mean we are using OpenAI as the provider, it just means we are using the OpenAI
 * wire format but the actual request could be handled by Baseten as a provider.
 */
export type AiClientType = Static<typeof AiClientTypeLiteral>;
export const AiClientType = getNormalEnum(AiClientTypeLiteral);

export const RealAiProviderTypeLiteral = Union(
  Literal("OPENAI"),
  Literal("ANTHROPIC"),
  Literal("BASETEN"),
  Literal("BEDROCK"),
  /**
   * VERTEX = Gemini Enterprise Agent Platform (formerly Vertex AI).
   * This value gets persisted in MagicApiKey.providerName so would need a migration to update
   */
  Literal("VERTEX"),
);
export type RealAiProviderType = Static<typeof RealAiProviderTypeLiteral>;
export const RealAiProviderType = getNormalEnum(RealAiProviderTypeLiteral);

export const AiProviderTypeLiteral = Union(
  ...RealAiProviderTypeLiteral.alternatives,
  /**
   * The mock provider used in tests/e2e. Not a real company, but mock requests
   * still flow through provider-keyed metrics/billing/logging.
   */
  Literal("MOCK"),
);
/**
 * The company/platform that services a given AI request.
 */
export type AiProviderType = Static<typeof AiProviderTypeLiteral>;
export const AiProviderType = getNormalEnum(AiProviderTypeLiteral);

/**
 * Maps an AI client config to the provider that services it by default (i.e. when
 * the org has no BYOK key that overrides the provider).
 *
 * The existence of this utility is a little bit backwards, ideally this would
 * be done on the path we are actually resolving the credentials to use.
 */
export function defaultAiProviderForClientConfig({
  clientConfig,
}: {
  clientConfig: AiClientConfig;
}): AiProviderType {
  switch (clientConfig.type) {
    case AiClientType.OPENAI:
      return AiProviderType.OPENAI;
    case AiClientType.OPENAI_TYPEAHEAD:
      return AiProviderType.BASETEN;
    case AiClientType.OPENAI_CHAT_COMPLETIONS: {
      switch (clientConfig.params.model.gateway) {
        case ChatCompletionsGateway.BASETEN:
        case ChatCompletionsGateway.BASETEN_DEDICATED:
        case undefined:
          return AiProviderType.BASETEN;
        default:
          return assertNever(
            clientConfig.params.model.gateway,
            clientConfig.params.model.gateway,
          );
      }
    }
    case AiClientType.ANTHROPIC:
      return AiProviderType.ANTHROPIC;
    case AiClientType.MOCK:
      return AiProviderType.MOCK;
    default:
      return assertNever(
        clientConfig,
        (clientConfig as { type?: string }).type,
      );
  }
}

export function aiProviderSupportsClientType({
  clientType,
  providerType,
}: {
  clientType: AiClientType;
  providerType: AiProviderType;
}): boolean {
  switch (clientType) {
    case AiClientType.ANTHROPIC: {
      switch (providerType) {
        case AiProviderType.ANTHROPIC:
        case AiProviderType.BEDROCK:
        case AiProviderType.VERTEX:
          return true;
        case AiProviderType.OPENAI:
        case AiProviderType.BASETEN:
        case AiProviderType.MOCK:
          return false;
        default:
          guardNever(providerType, providerType);
          return false;
      }
    }

    case AiClientType.OPENAI: {
      switch (providerType) {
        case AiProviderType.ANTHROPIC:
        case AiProviderType.BEDROCK:
        case AiProviderType.VERTEX:
        case AiProviderType.MOCK:
        case AiProviderType.BASETEN:
          return false;
        case AiProviderType.OPENAI:
          return true;
        default:
          guardNever(providerType, providerType);
          return false;
      }
    }

    case AiClientType.OPENAI_CHAT_COMPLETIONS:
    case AiClientType.OPENAI_TYPEAHEAD: {
      switch (providerType) {
        case AiProviderType.ANTHROPIC:
        case AiProviderType.BEDROCK:
        case AiProviderType.VERTEX:
        case AiProviderType.MOCK:
        case AiProviderType.OPENAI:
          return false;
        case AiProviderType.BASETEN:
          return true;
        default:
          guardNever(providerType, providerType);
          return false;
      }
    }

    case AiClientType.MOCK: {
      if (providerType === AiProviderType.MOCK) {
        return true;
      } else {
        return false;
      }
    }

    default:
      guardNever(clientType, clientType);
      return false;
  }
}

/**
 * Maps AI client types to their canonical client types for legacy code paths that key
 * on a smaller `OPENAI | ANTHROPIC` set (e.g. prompt-template
 * variants).
 *
 * chat completions and typeahead uses OpenAI clients under the hood
 */
export function resolveAiClientType(
  clientType: RealAiClientType,
): "BASETEN" | "OPENAI" | "ANTHROPIC" {
  switch (clientType) {
    case AiClientType.OPENAI_CHAT_COMPLETIONS:
      return "OPENAI";
    case AiClientType.OPENAI_TYPEAHEAD:
      return "BASETEN";
    default:
      return clientType;
  }
}

export const StreamingKey = Union(
  RRecord({
    key: String,
    type: Literal("STRING"),
  }),
  RRecord({ key: RArray(String), type: Literal("MULTI_OBJECT") }),
  RRecord({
    key: String,
    type: Literal("OBJECT_ARRAY"),
  }),
);
export type StreamingKey = Static<typeof StreamingKey>;

export const MockToolCalls = RArray(
  RRecord({
    arguments: RRecord({}),
    name: String,
  }).And(
    Partial({
      thinking: Optional(RArray(String)),
    }),
  ),
);
export const MockFunction = RRecord({
  streamingKey: Optional(StreamingKey),
  function: Dictionary(Unknown, String),
});
const StringOrFunction = String.Or(MockFunction);
export const StringOrFunctionOrFailureType = Union(
  StringOrFunction,
  MagicEventFailureReasonLiteral,
);
export type StringOrFunctionOrFailureType = Static<
  typeof StringOrFunctionOrFailureType
>;
export const MockResponse = Union(
  StringOrFunction,
  MagicEventFailureReasonLiteral,
  MockToolCalls,
);
export type MockResponse = Static<typeof MockResponse>;

/**
 * A group of mock responses that will be dispatched concurrently by the caller
 * (e.g. via `Promise.all`). The mock provider picks the matching entry by the
 * requested tool name instead of FIFO order, so the queue stays deterministic
 * even when two parallel `callLLM` invocations race to consume it.
 *
 * Falls back to FIFO within the group when no entry has a matching tool name
 * (e.g. for plain-text responses).
 */
export const MockResponseParallelGroup = RRecord({
  parallel: RArray(MockResponse),
});
export type MockResponseParallelGroup = Static<
  typeof MockResponseParallelGroup
>;

/**
 * A single entry in `responseQueue`. Most entries are plain `MockResponse`s
 * (consumed FIFO). Use `{ parallel: [...] }` to denote a set of responses that
 * are awaited concurrently in production code and should therefore be
 * dispatched by tool name instead of arrival order.
 */
export const MockResponseQueueEntry = Union(
  MockResponse,
  MockResponseParallelGroup,
);
export type MockResponseQueueEntry = Static<typeof MockResponseQueueEntry>;

export const MockClientModelParams = RRecord({
  model: Union(Literal("MOCK_PRIMARY_MODEL"), Literal("MOCK_FALLBACK_MODEL")),
  sleepTime: Optional(Number),
  /**
   * Map prompt template type to a response. Use for
   * one-shot prompts.
   */
  responseMap: Dictionary(
    Optional(
      Union(
        StringOrFunctionOrFailureType,
        Dictionary(StringOrFunctionOrFailureType, String),
      ),
    ),
    PromptTemplateTypeLiteral,
  ),
}).And(
  Partial({
    /**
     * Response queue, agent will make these calls in order. Use for agents.
     *
     * Use `{ parallel: [response1, response2] }` for sets of responses that
     * are awaited concurrently in production code (e.g. via `Promise.all`).
     * The mock dispatches within a parallel group by tool name so the queue
     * doesn't depend on which arm of the `Promise.all` happens to call the
     * mock first.
     */
    responseQueue: RArray(MockResponseQueueEntry),
  }),
);
export type MockClientModelParams = Static<typeof MockClientModelParams>;

const AiClientConfigBase = RRecord({
  type: AiClientTypeLiteral,
});

export const MockAiClientConfig = AiClientConfigBase.extend({
  type: Literal(AiClientType.MOCK),
  params: MockClientModelParams,
});
export type MockAiClientConfig = Static<typeof MockAiClientConfig>;

export const OpenAiClientConfig = AiClientConfigBase.extend({
  type: Literal(AiClientType.OPENAI),
  params: OpenAiModelParams,
});
export type OpenAiClientConfig = Static<typeof OpenAiClientConfig>;

export const OpenAiChatCompletionsClientConfig = AiClientConfigBase.extend({
  type: Literal(AiClientType.OPENAI_CHAT_COMPLETIONS),
  params: ChatCompletionsModelParams,
});
export type OpenAiChatCompletionsClientConfig = Static<
  typeof OpenAiChatCompletionsClientConfig
>;

export const AnthropicClientConfig = AiClientConfigBase.extend({
  type: Literal(AiClientType.ANTHROPIC),
  params: AnthropicModelParams,
});
export type AnthropicClientConfig = Static<typeof AnthropicClientConfig>;

export const OpenAITypeAheadClientConfig = RRecord({
  modelKeys: RRecord({
    us: String,
    eu: String,
    dev: String,
  }),
  maxInputTokens: Number,
  maxOutputTokens: Optional(Number), // Not used by reasoning models
});
export type OpenAITypeAheadClientConfig = Static<
  typeof OpenAITypeAheadClientConfig
>;

// Should approximately match the OpenAI model params,
// but with a different model type
export const BasetenLLMModelParams = RRecord({
  frequencyPenalty: Number,
  n: Number,
  presencePenalty: Number,
  stopWords: RArray(String),
  temperature: Optional(Number), // Not used by reasoning models
  topP: Number,
  reasoningEffort: Optional(
    Union(Literal("low"), Literal("medium"), Literal("high")),
  ), // ONLY used by reasoning models
  model: OpenAITypeAheadClientConfig,
});
export type BasetenLLMModelParams = Static<typeof BasetenLLMModelParams>;

export const OpenAiTypeaheadClientConfig = AiClientConfigBase.extend({
  type: Literal(AiClientType.OPENAI_TYPEAHEAD),
  params: BasetenLLMModelParams,
});
export type OpenAiTypeaheadClientConfig = Static<
  typeof OpenAiTypeaheadClientConfig
>;

export const AiClientConfig = Union(
  AnthropicClientConfig,
  MockAiClientConfig,
  OpenAiClientConfig,
  OpenAiChatCompletionsClientConfig,
  OpenAiTypeaheadClientConfig,
);
export type AiClientConfig = Static<typeof AiClientConfig>;

export const AiStaticClientConfigNameLiteral = Union(
  Literal("OPENAI_GPT_41"),
  Literal("OPENAI_GPT_41_REASONING"),
  Literal("OPENAI_GPT_54"),
  Literal("OPENAI_GPT_54_LOW_REASONING"),
  Literal("OPENAI_GPT_54_LOW_REASONING_LOW_VERBOSITY"),
  Literal("OPENAI_GPT_54_MEDIUM_REASONING"),
  Literal("OPENAI_GPT_54_HIGH_REASONING"),
  Literal("OPENAI_GPT_54_XHIGH_REASONING"),
  Literal("OPENAI_GPT_55"),
  Literal("OPENAI_GPT_55_LOW_REASONING"),
  Literal("OPENAI_GPT_55_MEDIUM_REASONING"),
  Literal("OPENAI_GPT_55_HIGH_REASONING"),
  Literal("OPENAI_GPT_55_XHIGH_REASONING"),
  Literal("OPENAI_GPT_56"),
  Literal("OPENAI_GPT_56_LOW_REASONING"),
  Literal("OPENAI_GPT_56_LOW_REASONING_FAST"),
  Literal("OPENAI_GPT_56_MEDIUM_REASONING"),
  Literal("OPENAI_GPT_56_MEDIUM_REASONING_FAST"),
  Literal("OPENAI_GPT_56_HIGH_REASONING"),
  Literal("OPENAI_GPT_56_HIGH_REASONING_FAST"),
  Literal("OPENAI_GPT_56_XHIGH_REASONING"),
  Literal("OPENAI_GPT_56_XHIGH_REASONING_FAST"),
  Literal("OPENAI_GPT_56_TERRA"),
  Literal("OPENAI_GPT_56_TERRA_LOW_REASONING"),
  Literal("OPENAI_GPT_56_TERRA_MEDIUM_REASONING"),
  Literal("OPENAI_GPT_56_TERRA_HIGH_REASONING"),
  Literal("OPENAI_GPT_56_TERRA_XHIGH_REASONING"),
  Literal("OPENAI_GPT_56_LUNA"),
  Literal("OPENAI_GPT_56_LUNA_LOW_REASONING"),
  Literal("OPENAI_GPT_56_LUNA_MEDIUM_REASONING"),
  Literal("OPENAI_GPT_56_LUNA_HIGH_REASONING"),
  Literal("OPENAI_GPT_56_LUNA_XHIGH_REASONING"),
  Literal("OPENAI_ULTIMA_ALPHA"),
  Literal("OPENAI_ULTIMA_ALPHA_LOW_REASONING"),
  Literal("OPENAI_ULTIMA_ALPHA_MEDIUM_REASONING"),
  Literal("OPENAI_ULTIMA_ALPHA_HIGH_REASONING"),
  Literal("OPENAI_ULTIMA_ALPHA_XHIGH_REASONING"),
  Literal("OPENAI_ULTIMA_ALPHA_MAX_REASONING"),
  Literal("OPENAI_GPT_54_MINI"),
  Literal("OPENAI_GPT_54_MINI_LOW_REASONING"),
  Literal("OPENAI_GPT_54_MINI_LOW_REASONING_LOW_VERBOSITY"),
  Literal("OPENAI_GPT_54_MINI_MEDIUM_REASONING"),
  Literal("OPENAI_GPT_54_MINI_HIGH_REASONING"),
  Literal("OPENAI_GPT_54_MINI_XHIGH_REASONING"),
  Literal("ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_SONNET_SMALL_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_SONNET_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_HAIKU"),
  Literal("ANTHROPIC_CLAUDE_4_5_HAIKU_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_OPUS"),
  Literal("ANTHROPIC_CLAUDE_4_6_OPUS_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_OPUS_LOW_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_OPUS_MEDIUM_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_OPUS_HIGH_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_OPUS_MAX_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_LOW_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_LOW_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_MEDIUM_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_MEDIUM_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_HIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_XHIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_7_OPUS_MAX_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_HIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_HIGH_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_XHIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_XHIGH_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_MAX_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_8_OPUS_MAX_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_LOW_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_LOW_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_LOW_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_HIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_HIGH_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_XHIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_XHIGH_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_MAX_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_OPUS_5_MAX_EFFORT_FAST"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5_LOW_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5_LOW_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5_MEDIUM_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5_MEDIUM_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5_HIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_FABLE_5_MAX_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET_LOW_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET_LOW_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET_MEDIUM_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET_HIGH_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_6_SONNET_MAX_EFFORT_THINKING"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_HIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_XHIGH_EFFORT"),
  Literal("ANTHROPIC_CLAUDE_5_SONNET_MAX_EFFORT"),
  Literal("BASETEN_DEDICATED_KIMI_K_2_7_THINKING"),
  Literal("BASETEN_DEDICATED_QWEN_3_6_35B_A3B"),
  Literal("BASETEN_DEFAULT_TYPEAHEAD"),
);
export type AiStaticClientConfigName = Static<
  typeof AiStaticClientConfigNameLiteral
>;
export const AiStaticClientConfigName = getNormalEnum(
  AiStaticClientConfigNameLiteral,
);

export const ModelFeatureKeyLiteral = Union(
  Literal("logic-view-agent"),
  Literal("ask-agent"),
  Literal("review-agent"),
  Literal("semantic-authoring-agent"),
  Literal("tao-project-agent"),
  Literal("data-discovery-agent"),
  Literal("default-one-shot"),
  Literal("fix-code-basic"),
  Literal("standard-llm-rubric"),
  Literal("confusion-detection"),
  Literal("retrieval-failure-detection"),
  Literal("hex-content-generation"),
  Literal("typeahead"),
  Literal("generate-suggested-prompts-sync"),
  Literal("app-agent"),
  Literal("scheduled-run-summary"),
  Literal("thread-topic-classification"),
  Literal("suggestion-source-grouping"),
  Literal("visualization-subagent"),
  Literal("data-app-subagent"),
  Literal("memory-extraction"),
  Literal("project-and-thread-naming"),
  Literal("subagent-work-summarization"),
  Literal("user-context-exhaust-detection"),
  Literal("thread-artifact-extraction"),
  Literal("hero-analyzer-agent"),
  Literal("hero-aggregator-agent"),
  Literal("knowledge-curation-agent"),
  Literal("customer-eval-judge-thread"),
  Literal("customer-eval-judge-final-answer"),
  Literal("customer-eval-numeric-extraction"),
  // Used for testing
  Literal("mock-agent"),
);

export type ModelFeatureKey = Static<typeof ModelFeatureKeyLiteral>;
export const ModelFeatureKey = getNormalEnum(ModelFeatureKeyLiteral);

export const UserFacingModelEntry = Union(
  RRecord({ kind: Literal("model"), model: String }),
  RRecord({ kind: Literal("comingSoon"), model: String }),
);

export const ModelFeatureConfig = RRecord({
  features: Dictionary(
    RArray(
      // We have to union String here to allow deploying new static configs
      // without breaking the validation in RC and prod.
      Union(AiClientConfig, AiStaticClientConfigNameLiteral, String),
    ),
    String,
  ),
  // Ordered list of picker entries. Each entry is either a selectable model or a
  // coming-soon row; both resolve against USER_FACING_MODEL_CONFIGS by Model.
  // Authoring order is preserved as render order.
  userFacingModels: RArray(UserFacingModelEntry).optional(),
});
/**
 * Manually narrow the compile-time type to be more specific and only allow
 * keys in the features object to be ModelFeatureKey's. This is necessary
 * because Runtypes would fail to validate nested extra keys in a dictionary
 * defined based on a literal string union, so we broaden the runtype to allow
 * any string key, and then manually narrow the typescript type to ensure
 * compile-time compliance.
 */
export type ModelFeatureConfig = Static<typeof ModelFeatureConfig> & {
  features: Record<
    ModelFeatureKey,
    Array<AiClientConfig | AiStaticClientConfigName>
  > &
    // Add a catch-all for unknown keys to ensure this type is as correct as possible
    Record<string, unknown>;
};

export const DEFAULT_MODEL_FEATURE_STATIC_CONFIGS: Record<
  ModelFeatureKey,
  AiStaticClientConfigName[]
> = {
  "app-agent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "ask-agent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "tao-project-agent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "review-agent": [
    "OPENAI_GPT_54_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_4_6_OPUS_LOW_EFFORT_THINKING",
  ],
  "confusion-detection": [
    "OPENAI_GPT_54",
    "ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING",
  ],
  "data-discovery-agent": [
    "ANTHROPIC_CLAUDE_4_5_HAIKU_THINKING",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "default-one-shot": ["OPENAI_GPT_54_MINI", "ANTHROPIC_CLAUDE_4_6_SONNET"],
  "fix-code-basic": ["OPENAI_GPT_54_MINI", "ANTHROPIC_CLAUDE_4_6_SONNET"],
  "generate-suggested-prompts-sync": [
    "OPENAI_GPT_54_MINI",
    "ANTHROPIC_CLAUDE_4_6_SONNET",
  ],
  "hex-content-generation": [
    "OPENAI_GPT_54_MINI",
    "ANTHROPIC_CLAUDE_4_6_SONNET",
  ],
  "logic-view-agent": [
    "OPENAI_GPT_56_MEDIUM_REASONING",
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
  ],
  "memory-extraction": ["ANTHROPIC_CLAUDE_4_5_HAIKU", "OPENAI_GPT_54"],
  "project-and-thread-naming": [
    "OPENAI_GPT_54_MINI",
    "ANTHROPIC_CLAUDE_4_5_HAIKU",
  ],
  "mock-agent": [],
  "scheduled-run-summary": [
    "OPENAI_GPT_54_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_4_6_OPUS",
  ],
  "semantic-authoring-agent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "standard-llm-rubric": [
    "ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING",
    "OPENAI_GPT_54",
  ],
  "suggestion-source-grouping": [
    "OPENAI_GPT_54_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_4_6_OPUS",
  ],
  "retrieval-failure-detection": [
    "OPENAI_GPT_54",
    "ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING",
  ],
  "thread-topic-classification": [
    "ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING",
    "OPENAI_GPT_54",
  ],
  typeahead: ["BASETEN_DEFAULT_TYPEAHEAD"],
  "visualization-subagent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "data-app-subagent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "subagent-work-summarization": [
    "ANTHROPIC_CLAUDE_4_5_HAIKU",
    "ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING",
    "OPENAI_GPT_54_MINI",
    "OPENAI_GPT_54",
  ],
  "user-context-exhaust-detection": [
    "ANTHROPIC_CLAUDE_4_6_OPUS",
    "OPENAI_GPT_56",
  ],
  "thread-artifact-extraction": ["ANTHROPIC_CLAUDE_4_6_OPUS", "OPENAI_GPT_56"],
  "hero-analyzer-agent": [
    "BASETEN_DEDICATED_KIMI_K_2_7_THINKING",
    "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
  ],
  "hero-aggregator-agent": [
    "OPENAI_GPT_56_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_4_7_OPUS_MEDIUM_EFFORT",
  ],
  "knowledge-curation-agent": [
    "OPENAI_GPT_56_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT",
  ],
  "customer-eval-judge-thread": [
    "OPENAI_GPT_56_MEDIUM_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT_NO_THINKING",
  ],
  "customer-eval-judge-final-answer": [
    "OPENAI_GPT_56_LOW_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "customer-eval-numeric-extraction": [
    "OPENAI_GPT_54_LOW_REASONING_LOW_VERBOSITY",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
};

export const DEFAULT_USER_FACING_MODELS: Static<typeof UserFacingModelEntry>[] =
  [
    { kind: "model", model: Model.CLAUDE_OPUS_5 },
    { kind: "model", model: Model.CLAUDE_4_8_OPUS },
    { kind: "model", model: Model.CLAUDE_5_SONNET },
    { kind: "model", model: Model.CLAUDE_FABLE_5 },
    { kind: "model", model: Model.GPT_56 },
    { kind: "model", model: Model.GPT_56_TERRA },
    { kind: "model", model: Model.GPT_56_LUNA },
    { kind: "model", model: Model.KIMI_K_2_7_THINKING },
  ];

export const DEFAULT_MODEL_FEATURE_CONFIG = {
  features: DEFAULT_MODEL_FEATURE_STATIC_CONFIGS,
  userFacingModels: DEFAULT_USER_FACING_MODELS,
} satisfies ModelFeatureConfig;

/**
 * Per-client entry of the `model-provider-config` flag. Declares the ordered
 * list of providers to try for a given AI client, with optional per-model
 * overrides.
 */
export const zModelProviderClientConfig = z.object({
  // Required fallback provider order for any model without an explicit override.
  defaultProviderOrder: z.array(z.nativeEnum(AiProviderType)),
  // Optional per-model overrides, keyed by model name (e.g. CLAUDE_4_7_SONNET).
  // A string key keeps forward-compat when a newer model name appears before the
  // enum is updated.
  models: z
    .record(
      z.string(),
      z.object({ providerOrder: z.array(z.nativeEnum(AiProviderType)) }),
    )
    .optional(),
});
export type ModelProviderClientConfig = z.infer<
  typeof zModelProviderClientConfig
>;

/**
 * Schema for the `model-provider-config` LaunchDarkly flag. Maps a model to an
 * ordered list of providers (e.g. Anthropic direct -> Bedrock -> Vertex),
 * scoped per AI client. Provider order is required per client; there is no
 * cross-client root default because providers do not overlap across clients.
 *
 * Resolution precedence (see getModelProviderOrder in @hex/server):
 *   clients[clientType].models[model].providerOrder
 *     -> clients[clientType].defaultProviderOrder
 *
 * BYOK always overrides this config.
 */
export const zModelProviderConfig = z.object({
  // Required. Keyed by AiClientType (e.g. ANTHROPIC).
  clients: z.record(z.string(), zModelProviderClientConfig),
});
export type ModelProviderConfig = z.infer<typeof zModelProviderConfig>;

export const DEFAULT_MODEL_PROVIDER_CONFIG: ModelProviderConfig = {
  clients: {
    [AiClientType.ANTHROPIC]: {
      defaultProviderOrder: [
        AiProviderType.ANTHROPIC,
        // TODO: This provider order system is currently in development
        // and we don't actually support any providers other than Anthropic
        // for non-BYOK use cases yet. Eventually this will allow us to fall
        // back to Bedrock and Vertex.
        // AiProviderType.BEDROCK,
        // AiProviderType.VERTEX,
      ],
    },
  },
};

export const OpenAiApiConfig = RRecord({
  organization: String.optional(),
  apiKey: String,
  baseURL: String.optional(),
});
export type OpenAiApiConfig = Static<typeof OpenAiApiConfig>;
export const MagicApiConfig = Union(OpenAiApiConfig);
export type MagicApiConfig = Static<typeof MagicApiConfig>;

export const MagicTypeAheadProviderConfig = RRecord({
  provider: MagicTypeAheadProviderLiteral,
  config: RRecord({}),
});
export type MagicTypeAheadProviderConfig = Static<
  typeof MagicTypeAheadProviderConfig
>;

export const MagicAcceptTypeLiteral = Union(
  Literal("KEEP"),
  Literal("KEEP_AND_RUN"),
  Literal("KEEP_IN_NEW_CELL"),
  Literal("KEEP_AND_RUN_IN_NEW_CELL"),
  Literal("KEEP_AND_EDIT"),
  Literal("REJECT"),
);
export type MagicAcceptType = Static<typeof MagicAcceptTypeLiteral>;
export const MagicAcceptType = getNormalEnum(MagicAcceptTypeLiteral);

export const MagicCancelReasonLiteral = Union(
  Literal("MANUAL"),
  Literal("GEN_CODE_ERROR"),
  Literal("TIMEOUT"),
  Literal("NO_DATA"),
  Literal("UNSUPPORTED"),
  Literal("DEFLECTION"),
);
export type MagicCancelReason = Static<typeof MagicCancelReasonLiteral>;
export const MagicCancelReason = getNormalEnum(MagicCancelReasonLiteral);

export const AgentFeedbackTypeLiteral = Union(
  Literal("THUMBS"),
  Literal("FLAG"),
);
export type AgentFeedbackType = Static<typeof AgentFeedbackTypeLiteral>;
export const AgentFeedbackType = getNormalEnum(AgentFeedbackTypeLiteral);

export const FlagForReviewReasonLiteral = Union(
  Literal("MISSING_CONTEXT"),
  Literal("BAD_DATA_SELECTION"),
  Literal("UNEXPECTED_BEHAVIOR"),
);
export type FlagForReviewReason = Static<typeof FlagForReviewReasonLiteral>;
export const FlagForReviewReason = getNormalEnum(FlagForReviewReasonLiteral);

export function isFlagForReviewReason(
  reason: string | null | undefined,
): reason is FlagForReviewReason {
  return reason != null && FlagForReviewReasonLiteral.guard(reason);
}

// Subset of FlagForReviewReason that we treat as actionable feedback for the review agent
export const ACTIONABLE_FLAG_FOR_REVIEW_AGENT: ReadonlySet<FlagForReviewReason> =
  new Set([
    FlagForReviewReason.MISSING_CONTEXT,
    FlagForReviewReason.BAD_DATA_SELECTION,
  ]);

export function isActionableFlagForReviewReason(
  reason: string | null | undefined,
): reason is FlagForReviewReason {
  return (
    isFlagForReviewReason(reason) &&
    ACTIONABLE_FLAG_FOR_REVIEW_AGENT.has(reason)
  );
}

export const FLAG_REASON_LABELS: Record<FlagForReviewReason, string> = {
  [FlagForReviewReason.MISSING_CONTEXT]: "Missing context or information",
  [FlagForReviewReason.BAD_DATA_SELECTION]: "Bad table/data selection",
  [FlagForReviewReason.UNEXPECTED_BEHAVIOR]: "Unexpected behavior",
};

export function getFlagReasonLabel(reason: string | null): string {
  return isFlagForReviewReason(reason)
    ? FLAG_REASON_LABELS[reason]
    : FLAG_REASON_LABELS[FlagForReviewReason.UNEXPECTED_BEHAVIOR];
}

export const ColumnReference = RRecord({
  name: String,
  type: String,
  description: Nullish(String),
  dataManagerDescription: Optional(String),
  vectorDistance: Optional(Number),
  // Populated for columns of semantic-layer-backed tables (e.g. Snowflake
  // semantic views, Databricks metric views). MEASURE / DIMENSION / FACT.
  semanticLayerType: Optional(
    Union(Literal("MEASURE"), Literal("DIMENSION"), Literal("FACT")),
  ),
  // For semantic views, the underlying logical-table name the column lives on.
  // Used in Snowflake `SEMANTIC_VIEW(<view> METRICS <sourceTable.column>)`.
  sourceTableName: Nullish(String),
});
export type ColumnReference = Static<typeof ColumnReference>;

// TODO: Seems bad to just duplicate this definition, but it won't import correctly from hex_sl_schema...
export const MeasureFilter = RRecord({
  sql: Optional(String.Or(Null)),
  calc: Optional(Union(String, RRecord({})).Or(Null)),
});
export type MeasureFilter = Static<typeof MeasureFilter>;

export const SemanticDatasetColumnReference = ColumnReference.extend({
  semanticType: Literal("MEASURE").Or(Literal("DIMENSION")),
  filters: Optional(RArray(MeasureFilter)),
});
export type SemanticDatasetColumnReference = Static<
  typeof SemanticDatasetColumnReference
>;

export const MagicDataframeReferenceTypeLiteral = Union(
  Literal("DATAFRAME"),
  Literal("SNOWPARK_DATAFRAME"),
  Literal("REMOTE_DATAFRAME"),
  Literal("BIGFRAMES_DATAFRAME"),
);
export const MagicDataframeReferenceType = getNormalEnum(
  MagicDataframeReferenceTypeLiteral,
);

export const MagicTableReferenceTypeLiteral = Literal("TABLE");
export const MagicSemanticDatasetReferenceTypeLiteral =
  Literal("SEMANTIC_DATASET");

export const MagicReferenceTypeLiteral = Union(
  ...MagicDataframeReferenceTypeLiteral.alternatives,
  MagicSemanticDatasetReferenceTypeLiteral,
  MagicTableReferenceTypeLiteral,
);
export const MagicReferenceType = getNormalEnum(MagicReferenceTypeLiteral);
export type MagicReferenceType = Static<typeof MagicReferenceTypeLiteral>;

export const ReferenceInfo = RRecord({
  dataConnectionId: Optional(DataConnectionId),
  table: String,
  type: MagicReferenceTypeLiteral,
  tableId: Optional(DataSourceTableId),
  tableDescription: Nullish(String),
  schema: String.Or(Null),
  schemaId: Optional(DataSourceSchemaId),
  database: String.Or(Null),
  databaseId: Optional(DataSourceDatabaseId),
  columns: RArray(ColumnReference),
  dataManagerDescription: Optional(String),
  tablePrioritized: Boolean.Or(Null),
  schemaPrioritized: Boolean.Or(Null),
  databasePrioritized: Boolean.Or(Null),
  formatted: Optional(String),
  categories: Optional(RArray(String)),
  status: Optional(String.Or(Null)),
  // Optional table-type marker mirroring `DataSourceTable.tableType` for
  // semantic-layer-backed tables (e.g. "SEMANTIC_VIEW" for Snowflake semantic
  // views, "METRIC_VIEW" for Databricks metric views). Null/undefined for
  // regular warehouse tables.
  tableType: Nullish(Union(Literal("METRIC_VIEW"), Literal("SEMANTIC_VIEW"))),
});

export type ReferenceInfo = Static<typeof ReferenceInfo>;

export const SemanticDatasetReferenceInfo = ReferenceInfo.extend({
  database: Null,
  schema: Literal("_hex"),
  type: Literal("SEMANTIC_DATASET"),
  timeUnits: RArray(String),
  columns: RArray(SemanticDatasetColumnReference),
  joins: RArray(
    RRecord({
      name: String.Or(Null),
      target: String,
    }),
  ),
  validQueryPaths: RArray(RArray(String)),
});

export type SemanticDatasetReferenceInfo = Static<
  typeof SemanticDatasetReferenceInfo
>;
export const MagicClientReferenceInfo = Union(
  RRecord({
    id: DataSourceTableId,
    type: MagicTableReferenceTypeLiteral,
    name: String,
  }),
  RRecord({
    name: String,
    type: MagicDataframeReferenceTypeLiteral,
  }),
);

export type MagicClientReferenceInfo = Static<typeof MagicClientReferenceInfo>;

export const CELL_TYPE_BY_PROMPT_TEMPLATE = {
  [PromptTemplateType.GENERATE_SQL]: MagicCellType.SQL,
  [PromptTemplateType.GENERATE_PYTHON]: MagicCellType.CODE,
  [PromptTemplateType.SIMPLE_CHART]: MagicCellType.CHART,
};

// GeneratePromptComponentsOptions is used to generate the project context for a Magic Event
export const GeneratePromptComponentsOptions = RRecord({
  traceback: String.Or(Null), // Traceback of the current cell
  contentToEdit: String.Or(Null), // alternative text/code to edit (i.e. not the existing cell source); used for e.g. diff-based-editing autofixes
  agentGeneratedPrompt: String.Or(Null), // A prompt meant for an LLM that was generated by a previous call to the LLM (e.g., the 'Plan' in PlanExec).
  prevErrorDescription: String.Or(Null), // A description of an error the model previously made
  scheduledRunScreenshot: Optional(
    RRecord({
      base64Content: String,
      mimeType: MagicSupportedDocumentMimeTypes,
    }),
  ),
});

export type GeneratePromptComponentsOptions = Static<
  typeof GeneratePromptComponentsOptions
>;

export const MagicCellContentsReferences = RRecord({
  tableReferences: RArray(String),
});
export type MagicCellContentsReferences = Static<
  typeof MagicCellContentsReferences
>;

export const MagicLiteralType = Union(
  Literal("STRING"),
  Literal("FLOAT"),
  Literal("INTEGER"),
  Literal("BOOLEAN"),
  Literal("DATETIME"),
);
export type MagicLiteralType = Static<typeof MagicLiteralType>;

export const MagicExploreColumnRef = RRecord({
  column: String,
  db: String.Or(Null),
  // schema and table should never be null for new explore generations, but are nullable for
  // backcompat with previously generated suggested prompts
  schema: String.Or(Null),
  table: String.Or(Null),
});
export type MagicExploreColumnRef = Static<typeof MagicExploreColumnRef>;

export const MagicExploreStubField = RRecord({
  columns: RArray(MagicExploreColumnRef), // Array of column names that this field depends on
  alias: String.Or(Null), // Alias for the field, if any
  identifier: String, // The raw identifier for the field (either a calc expression, column name, literal, or "*")
  type: MagicLiteralType.Or(Null), // The type of the field, if it's a literal
  agg: HqlAggregationFunctionLiteral.Or(Null), // The aggregation to apply to the field in the explore spec
  calc: Boolean, // Whether the field is a calculated field
});

export type MagicExploreStubField = Static<typeof MagicExploreStubField>;

export const UnconsumedQueryElementCategoryLiteral = Union(
  Literal("JOIN"),
  Literal("FILTER"),
  Literal("SELECT"),
  Literal("ORDER"),
  Literal("ROOT"),
  Literal("WINDOW"),
);
export type UnconsumedQueryElementCategory = Static<
  typeof UnconsumedQueryElementCategoryLiteral
>;
export const UnconsumedQueryElementCategory = getNormalEnum(
  UnconsumedQueryElementCategoryLiteral,
);

export const MagicExploreRelativeDateField = RRecord({
  amount: Number,
  unit: RelativeDateUnitLiteral,
  operation: RelativeDateOperationLiteral,
  column: MagicExploreColumnRef,
  calc: Literal(false),
});
export type MagicExploreRelativeDateField = Static<
  typeof MagicExploreRelativeDateField
>;

export const UnconsumedQueryElements = Dictionary(
  RArray(String),
  UnconsumedQueryElementCategoryLiteral,
);
export type UnconsumedQueryElements = Static<typeof UnconsumedQueryElements>;

export const MagicExploreStubFilterPredicate = Union(
  ListBinaryColumnPredicateOpLiteral,
  BinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
);

export const MagicExploreSqlParseOrderBy = RRecord({
  by: RArray(MagicExploreStubField),
  desc: Boolean,
}).Or(Null);

export type MagicExploreSqlParseOrderBy = Static<
  typeof MagicExploreSqlParseOrderBy
>;

export const MagicExploreStubFilters = RArray(
  RRecord({
    predicate: MagicExploreStubFilterPredicate,
    a: Union(MagicExploreStubField, MagicExploreRelativeDateField),
    b: Union(RArray(MagicExploreStubField), MagicExploreStubField, Null),
  }),
);
export type MagicExploreStubFilters = Static<typeof MagicExploreStubFilters>;

export const MagicExploreStubJoins = RArray(
  RRecord({
    baseTable: MagicTableReference,
    table: MagicTableReference,
    on: RRecord({
      a: MagicExploreStubField,
      b: MagicExploreStubField,
    }),
  }),
).Or(Null);

export type MagicExploreStubJoins = Static<typeof MagicExploreStubJoins>;

export const MagicExploreSqlParse = RRecord({
  // All fields selected in the query
  fields: RArray(MagicExploreStubField),
  // Unconsumed query elements, grouped by category If non-empty, indicates the agent response is
  // not expressible in the explore UX
  unconsumedQueryElements: UnconsumedQueryElements,
  // Array of AND-ed filters in the query. Because the explore UX does not support OR filters,
  // those are represented as calculated fields + a simple IS_TRUE filter on the calculated column.
  filters: MagicExploreStubFilters,
  // The table the agent selected from in the query
  table: MagicTableReference,
  // The limit clause in the query --> Not currently used
  limit: Number.Or(Null),
  // Specification of the fields to order by in the query
  order: MagicExploreSqlParseOrderBy,
  joins: MagicExploreStubJoins,
});
export type MagicExploreSqlParse = Static<typeof MagicExploreSqlParse>;

export const UserMagicConfig = RRecord({
  magicEnabled: Boolean,
  magicTypeaheadAllowed: Boolean,
  onboardedToMagic: Boolean,
});
export type UserMagicConfig = Static<typeof UserMagicConfig>;

// ProjectContextForPromptGeneration is the context used to generate Prompt Components for a Magic Event
export const ProjectContextForPromptGeneration = RRecord({
  previousPrompts: RArray(
    RRecord({ prompt: String, referencedTables: RArray(MagicTableReference) }),
  ), // Previous prompts in the project
  existingChainCells: RArray(MagicPromptGenerationCellSpec), // Existing cells in the current chain to be generated
  projectCategories: RArray(
    RRecord({ name: String, description: String.Or(Null) }),
  ), // Categories of the project
  projectCellSources: RArray(
    RRecord({
      contents: String,
      title: String,
    }),
  ), // Contents and title of each cell in the project
  projectTitle: String, // Title of the project
  projectVariables: RArray(VariableName), // Existing in-scope variables for the project that the agent should not overwrite in its generated code
  projectImports: RArray(String), // Existing imports in the project that the agent should not include in its generated code
  previousEdits: RArray(
    RRecord({
      prompt: String,
      result: String,
    }),
  ),
  previousExploreEdits: RArray(
    RRecord({
      id: MagicEventId,
      prompt: String,
      result: String.Or(Null),
      functionArguments: String.Or(Null),
      type: MagicKeywordLiteral,
    }),
  ),
  projectStatus: RRecord({
    name: String,
    description: String.Or(Null),
  }),
  magicRules: String.Or(Null),
  chainContents: RArray(
    RRecord({ cellType: MagicCellTypeLiteral, contents: String }),
  ),
  lastCellOutput: DisplayTableOutput,
  targetCellContents: String,
  exploreImage: RRecord({
    base64Content: String,
    mimeType: MagicSupportedImageMimeTypes,
  }),
  scheduledRunScreenshot: RRecord({
    base64Content: String,
    mimeType: MagicSupportedDocumentMimeTypes,
  }),
  exploreOutputs: RRecord({
    table: Optional(DisplayTableOutput),
    rowCount: Optional(Number),
    pageSize: Optional(Number),
  }),
  exploreConfig: RRecord({
    sqlQuery: Optional(String), // Raw SQL query string the model generated.
    sqlParse: Optional(MagicExploreSqlParse), // Query string parsed to "magic stub" schema.
  }),
  previousError: RRecord({
    description: String, // A description of the failure that's causing us to retry the event.
    result: String, // The result of the previous event.
  }).Or(Null),
  relatedCellSources: RArray(
    RRecord({
      contents: String,
      language: String,
      title: String,
    }),
  ), // Contents and metadata of related cells for context
});

export type ProjectContextForPromptGeneration = Static<
  typeof ProjectContextForPromptGeneration
>;

// Magic API Key types

// These are fields we should mask from being returned to a user
export const SecureField = Union(
  Literal("apiKey"),
  Literal("privateKey"),
  Literal("privateKeyPassphrase"),
  Literal("serviceAccountJson"),
);
export type SecureField = Static<typeof SecureField>;

/**
 * Header names an org admin may configure as BYOK custom headers, covering
 * the common AI gateway auth headers. Names are matched case-insensitively
 * (HTTP header names are case-insensitive).
 *
 * The allowlist is enforced when saving new or changed headers; keys saved
 * before the allowlist existed may contain other header names and continue
 * to work at runtime.
 */
export const BYOK_CUSTOM_HEADER_ALLOWLIST: readonly string[] = [
  "Authorization",
  "x-api-key",
  "api-key",
  // Portkey
  "x-portkey-api-key",
  "x-portkey-virtual-key",
  "x-portkey-config",
  "x-portkey-provider",
];

export function isAllowedByokCustomHeader(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  return BYOK_CUSTOM_HEADER_ALLOWLIST.some(
    (allowed) => allowed.toLowerCase() === normalized,
  );
}

export const OpenAIMagicApiKeyDetails = RRecord({
  apiKey: String,
  openAIOrgId: String,
  baseURL: String.optional(),
  customHeaders: Dictionary(String, String).optional(),
});

export type OpenAIMagicApiKeyDetails = Static<typeof OpenAIMagicApiKeyDetails>;

const SharedAnthropicLikeMagicApiKeyDetails = RRecord({
  baseUrl: String.optional(),
  customModelMapping: Dictionary(
    String.optional(),
    AnthropicModelLiteral,
  ).optional(),
  customHeaders: Dictionary(String, String).optional(),
});

export const AnthropicMagicApiKeyDetails =
  SharedAnthropicLikeMagicApiKeyDetails.extend({
    apiKey: String,
  });

export type AnthropicMagicApiKeyDetails = Static<
  typeof AnthropicMagicApiKeyDetails
>;

export const BedrockMagicApiKeyDetails =
  SharedAnthropicLikeMagicApiKeyDetails.extend({
    apiKey: String,
    awsRegion: String,
  });
export type BedrockMagicApiKeyDetails = Static<
  typeof BedrockMagicApiKeyDetails
>;

export const GeminiMagicApiKeyDetails =
  SharedAnthropicLikeMagicApiKeyDetails.extend({
    /** @deprecated, use `multiRegion` instead */
    region: String.optional(),
    /**
     * Which region supports a given model for GEAP is a bit convoluted
     * so instead we support a 'psuedo' multi region that we map to
     * a specific region that works for a given model.
     */
    multiRegion: GeminiEAPMultiRegionLiteral.optional(),
    projectId: String,
    serviceAccountJson: String,
  }).withConstraint(
    (details) =>
      details.region != null ||
      details.multiRegion != null ||
      "Must specify either region or multiRegion",
  );
export type GeminiMagicApiKeyDetails = Static<typeof GeminiMagicApiKeyDetails>;

export const MagicApiKeyDetails = Union(
  RRecord({
    providerName: Literal(AiProviderType.OPENAI),
    details: OpenAIMagicApiKeyDetails,
  }),
  RRecord({
    providerName: Literal(AiProviderType.ANTHROPIC),
    details: AnthropicMagicApiKeyDetails,
  }),
  RRecord({
    providerName: Literal(AiProviderType.BEDROCK),
    details: BedrockMagicApiKeyDetails,
  }),
  RRecord({
    providerName: Literal(AiProviderType.VERTEX),
    details: GeminiMagicApiKeyDetails,
  }),
);
export type MagicApiKeyDetails = Static<typeof MagicApiKeyDetails>;

/**
 * The custom base URL (endpoint override) configured on a BYOK key, or
 * `undefined` when the key uses the provider default. OpenAI stores the value
 * as `baseURL`; the Anthropic-like providers store it as `baseUrl`.
 */
export function getMagicApiKeyBaseUrl(
  keyDetails: MagicApiKeyDetails,
): string | undefined {
  switch (keyDetails.providerName) {
    case AiProviderType.OPENAI:
      return keyDetails.details.baseURL ?? undefined;
    case AiProviderType.ANTHROPIC:
    case AiProviderType.BEDROCK:
    case AiProviderType.VERTEX:
      return keyDetails.details.baseUrl ?? undefined;
    default:
      guardNever(keyDetails, keyDetails);
      return undefined;
  }
}

/**
 * Whether a BYOK key is configured with a custom base URL (endpoint override).
 */
export function magicApiKeyUsesBaseUrl(
  keyDetails: MagicApiKeyDetails,
): boolean {
  const baseUrl = getMagicApiKeyBaseUrl(keyDetails);
  return baseUrl != null && baseUrl.length > 0;
}

/**
 * Whether a BYOK key is configured with any non-empty custom model name
 * mapping. OpenAI BYOK keys do not support custom model mappings.
 */
export function magicApiKeyUsesCustomModelNames(
  keyDetails: MagicApiKeyDetails,
): boolean {
  switch (keyDetails.providerName) {
    case AiProviderType.OPENAI:
      return false;
    case AiProviderType.ANTHROPIC:
    case AiProviderType.BEDROCK:
    case AiProviderType.VERTEX:
      return Object.values(keyDetails.details.customModelMapping ?? {}).some(
        (name) => name != null && name.length > 0,
      );
    default:
      guardNever(keyDetails, keyDetails);
      return false;
  }
}

// Invalid key specification returned from validation failure
export const InvalidMagicApiKey = RRecord({
  providerName: AiProviderTypeLiteral,
  message: String,
});
export type InvalidMagicApiKey = Static<typeof InvalidMagicApiKey>;

// The types of "dataframe" sources that an explore cell can have.
// This is used by magic to determine how different types of tables/columns/etc
// should be loaded.
export type ExploreSourceType =
  | "DATA_WAREHOUSE"
  | "DATAFRAME"
  | "SEMANTIC_DATASET";
